<script setup>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';

import { getContactTierName } from 'dashboard/components-next/Contacts/contactTiers';
import { getPreferredLanguageName } from 'dashboard/components-next/Contacts/preferredLanguages';
import ChannelIcon from 'dashboard/components-next/icon/ChannelIcon.vue';
import { formatPreferenceValue } from 'dashboard/helper/contactPreferences';
import { INBOX_TYPES } from 'dashboard/helper/inbox';
import countries from 'shared/constants/countries';

const props = defineProps({
  selectedContact: {
    type: Object,
    required: true,
  },
  createdAt: {
    type: String,
    default: '',
  },
  lastActivityAt: {
    type: String,
    default: '',
  },
});

const emit = defineEmits(['edit']);

const { t } = useI18n();
const mockRevenue = '24 500 EUR';

const additionalAttributes = computed(
  () => props.selectedContact?.additionalAttributes || {}
);

const countryName = computed(() => {
  const { country, countryCode } = additionalAttributes.value;
  if (country) return country;

  const selectedCountry = countries.find(item => item.id === countryCode);
  return selectedCountry?.name || '';
});

const location = computed(() =>
  [additionalAttributes.value.city, countryName.value]
    .filter(Boolean)
    .join(', ')
);

const preferredChannels = computed(() => ({
  whatsapp: {
    inbox: { channel_type: INBOX_TYPES.WHATSAPP },
    label: t('PURCHASE_PROJECTS.CHANNELS.WHATSAPP'),
  },
  sms: {
    inbox: { channel_type: INBOX_TYPES.SMS },
    label: t('PURCHASE_PROJECTS.CHANNELS.SMS'),
  },
  email: {
    inbox: { channel_type: INBOX_TYPES.EMAIL },
    label: t('PURCHASE_PROJECTS.CHANNELS.EMAIL'),
  },
}));

const preferredChannel = computed(
  () =>
    additionalAttributes.value.preferredChannel ||
    additionalAttributes.value.preferred_channel ||
    'whatsapp'
);

const preferredChannelConfig = computed(
  () =>
    preferredChannels.value[preferredChannel.value] ??
    preferredChannels.value.whatsapp
);

const normalizeInbox = inbox => ({
  ...inbox,
  avatar_url: inbox.avatar_url || inbox.avatarUrl,
  channel_id: inbox.channel_id || inbox.channelId,
  channel_type: inbox.channel_type || inbox.channelType,
  phone_number: inbox.phone_number || inbox.phoneNumber,
});

const getPreferredKeyForInbox = inbox => {
  const channelType = inbox.channel_type;
  const phoneNumber = inbox.phone_number || '';

  if (channelType === INBOX_TYPES.EMAIL) return 'email';
  if (channelType === INBOX_TYPES.WHATSAPP) return 'whatsapp';
  if (channelType === INBOX_TYPES.SMS) return 'sms';

  if (channelType === INBOX_TYPES.TWILIO) {
    return inbox.medium === 'whatsapp' || phoneNumber.startsWith('whatsapp')
      ? 'whatsapp'
      : 'sms';
  }

  return '';
};

const contactChannelInboxes = computed(() => {
  const contactInboxes =
    props.selectedContact?.contactInboxes ||
    props.selectedContact?.contact_inboxes ||
    [];
  const seenInboxIds = new Set();

  return contactInboxes
    .map(contactInbox => contactInbox?.inbox)
    .filter(Boolean)
    .map(normalizeInbox)
    .filter(inbox => {
      const inboxKey = inbox.id || `${inbox.channel_type}-${inbox.name}`;
      if (seenInboxIds.has(inboxKey)) return false;

      seenInboxIds.add(inboxKey);
      return true;
    });
});

const communicationChannels = computed(() => {
  const channels = contactChannelInboxes.value.map(inbox => {
    const preferredKey = getPreferredKeyForInbox(inbox);
    return {
      key: inbox.id || `${inbox.channel_type}-${inbox.name}`,
      label: inbox.name || preferredChannels.value[preferredKey]?.label || '',
      inbox,
      isPreferred: preferredKey === preferredChannel.value,
    };
  });

  const hasPreferredChannel = channels.some(channel => channel.isPreferred);
  if (!hasPreferredChannel) {
    channels.push({
      key: `preferred-${preferredChannel.value}`,
      label: preferredChannelConfig.value.label,
      inbox: preferredChannelConfig.value.inbox,
      isPreferred: true,
    });
  }

  return channels.filter(channel => channel.label);
});

const communicationChannelSummary = computed(() =>
  communicationChannels.value.map(channel => channel.label).join(', ')
);

const preferredLanguage = computed(
  () =>
    additionalAttributes.value.preferredLanguage ||
    additionalAttributes.value.preferred_language ||
    ''
);

const preferredLanguageName = computed(() =>
  getPreferredLanguageName(preferredLanguage.value)
);

const contactTier = computed(() => additionalAttributes.value.tier || '');

const contactTierName = computed(() =>
  getContactTierName(contactTier.value, t)
);

const informationRows = computed(() =>
  [
    {
      key: 'email',
      value: props.selectedContact?.email,
      icon: 'i-lucide-mail',
    },
    {
      key: 'phone-number',
      value: props.selectedContact?.phoneNumber,
      icon: 'i-lucide-phone',
    },
    {
      key: 'company-name',
      value: additionalAttributes.value.companyName,
      icon: 'i-lucide-building-2',
    },
    {
      key: 'contact-tier',
      label: t('CONTACTS_LAYOUT.DETAILS.TIER'),
      value: contactTierName.value,
      icon: 'i-lucide-gem',
      tier: true,
    },
    {
      key: 'mock-revenue',
      label: t('CONTACTS_LAYOUT.DETAILS.REVENUE'),
      value: mockRevenue,
      icon: 'i-lucide-euro',
    },
    {
      key: 'location',
      value: location.value,
      icon: 'i-lucide-map-pin',
    },
    {
      key: 'communication-channels',
      label: t('CONTACTS_LAYOUT.DETAILS.COMMUNICATION_CHANNELS'),
      value: communicationChannelSummary.value,
      icon: 'i-lucide-message-circle',
      channels: communicationChannels.value,
    },
    {
      key: 'preferred-language',
      label: t('CONTACTS_LAYOUT.DETAILS.PREFERRED_LANGUAGE'),
      value: preferredLanguageName.value,
      icon: 'i-lucide-languages',
    },
  ].filter(item => item.value)
);

const preferenceRows = computed(() => {
  const contactPreferences = props.selectedContact?.contactPreferences || [];
  return contactPreferences
    .map(preference => ({
      key: preference.key,
      label: preference.label,
      value: formatPreferenceValue(preference.value),
    }))
    .filter(item => item.value?.toString().trim());
});

const socialRows = computed(() => {
  const socialProfiles = additionalAttributes.value.socialProfiles || {};

  return [
    {
      key: 'facebook',
      value: socialProfiles.facebook,
      icon: 'i-ri-facebook-circle-fill',
    },
    {
      key: 'github',
      value: socialProfiles.github,
      icon: 'i-ri-github-fill',
    },
    {
      key: 'instagram',
      value: socialProfiles.instagram,
      icon: 'i-ri-instagram-line',
    },
    {
      key: 'telegram',
      value:
        socialProfiles.telegram ||
        additionalAttributes.value.socialTelegramUserName,
      icon: 'i-ri-telegram-fill',
    },
    {
      key: 'tiktok',
      value: socialProfiles.tiktok,
      icon: 'i-ri-tiktok-fill',
    },
    {
      key: 'linkedin',
      value: socialProfiles.linkedin,
      icon: 'i-ri-linkedin-box-fill',
    },
    {
      key: 'twitter',
      value: socialProfiles.twitter,
      icon: 'i-ri-twitter-x-fill',
    },
  ].filter(item => item.value);
});

const bio = computed(
  () => additionalAttributes.value.description?.trim() || ''
);

const formatValue = value => value?.toString().trim() || '';
</script>

<template>
  <section class="flex flex-col w-full gap-3">
    <!-- Block 1 : nom / date -->
    <div class="w-full px-4 py-5 bg-n-solid-1">
      <h1 class="text-xl font-semibold break-words text-n-slate-12">
        {{ selectedContact?.name }}
      </h1>
      <div class="flex flex-wrap gap-x-3 gap-y-1 mt-1 text-sm text-n-slate-11">
        <span
          v-if="selectedContact?.identifier"
          class="inline-flex items-center gap-1"
        >
          <span class="i-ph-user-gear text-n-slate-10 size-4" />
          {{ selectedContact.identifier }}
        </span>
        <span v-if="createdAt">
          {{ t('CONTACT_PANEL.CREATED_AT_RELATIVE', { time: createdAt }) }}
        </span>
        <span v-if="lastActivityAt">
          {{ t('CONTACTS_LAYOUT.FILTER.LAST_ACTIVITY') }} {{ lastActivityAt }}
        </span>
      </div>
    </div>

    <!-- Block 2 : Informations -->
    <div
      v-if="informationRows.length || bio || socialRows.length"
      class="w-full bg-n-solid-1"
    >
      <div class="flex items-center justify-between px-4 pb-4 pt-4">
        <h6 class="text-sm font-semibold text-n-slate-12">
          {{ t('CONTACTS_LAYOUT.DETAILS.INFORMATION_TITLE') }}
        </h6>
        <button
          type="button"
          class="rounded-full bg-n-alpha-black2 px-2.5 py-1 text-xs font-medium text-n-slate-11 transition-colors hover:text-n-slate-12"
          @click="emit('edit')"
        >
          {{ t('CONTACTS_LAYOUT.DETAILS.EDIT_INFORMATION') }}
        </button>
      </div>

      <template v-for="(item, index) in informationRows" :key="item.key">
        <div v-if="index > 0" class="h-px mx-4 bg-n-weak" />
        <div class="flex items-start gap-2 px-4 py-3 text-sm text-n-slate-12">
          <span
            class="size-4 mt-0.5 shrink-0 text-n-slate-10"
            :class="item.icon"
          />
          <span v-if="item.label" class="flex min-w-0 flex-col gap-0.5">
            <span class="text-xs text-n-slate-10">{{ item.label }}</span>
            <span
              v-if="item.channels"
              class="flex min-w-0 flex-wrap gap-x-2 gap-y-1"
            >
              <span
                v-for="channel in item.channels"
                :key="channel.key"
                class="inline-flex max-w-full items-center gap-1.5 text-sm text-n-slate-12"
              >
                <ChannelIcon :inbox="channel.inbox" class="size-3.5 shrink-0" />
                <span class="truncate">{{ channel.label }}</span>
                <span
                  v-if="channel.isPreferred"
                  class="shrink-0 rounded-full bg-n-brand/10 px-1.5 py-0.5 text-[10px] font-medium uppercase leading-none text-n-brand"
                >
                  {{ t('CONTACTS_LAYOUT.DETAILS.PREFERRED_CHANNEL_BADGE') }}
                </span>
              </span>
            </span>
            <span
              v-else-if="item.tier"
              class="inline-flex w-fit max-w-full rounded-full bg-n-amber-3 px-2 py-0.5 text-xs font-medium uppercase tracking-normal text-n-amber-11"
            >
              {{ formatValue(item.value) }}
            </span>
            <span v-else class="break-words">
              {{ formatValue(item.value) }}
            </span>
          </span>
          <span v-else class="break-words">{{ formatValue(item.value) }}</span>
        </div>
      </template>

      <p
        v-if="bio"
        class="px-4 py-3 text-sm leading-6 whitespace-pre-wrap text-n-slate-12"
      >
        {{ bio }}
      </p>

      <div
        v-if="socialRows.length"
        class="flex flex-wrap px-4 py-3 gap-x-4 gap-y-2"
      >
        <div
          v-for="item in socialRows"
          :key="item.key"
          class="inline-flex items-center gap-2 text-sm text-n-slate-12"
        >
          <span class="size-4 shrink-0 text-n-slate-10" :class="item.icon" />
          <span class="break-all">{{ item.value }}</span>
        </div>
      </div>

      <div class="pb-2" />
    </div>

    <!-- Block 3 : Preferences -->
    <div v-if="preferenceRows.length" class="w-full bg-n-solid-1">
      <div class="flex items-center justify-between px-4 pt-4 pb-3">
        <h6 class="text-sm font-semibold text-n-slate-12">
          {{ t('CONTACTS_LAYOUT.DETAILS.PREFERENCES_TITLE') }}
        </h6>
        <button
          type="button"
          class="flex items-center gap-1.5 rounded-full bg-n-alpha-black2 px-2.5 py-1 text-xs font-medium text-n-slate-11 hover:text-n-slate-12 transition-colors ml-auto"
          @click="emit('edit')"
        >
          <span class="i-lucide-pencil size-3.5" />
          {{ t('CONTACTS_LAYOUT.DETAILS.EDIT_INFORMATION') }}
        </button>
      </div>

      <div class="flex flex-col gap-1.5 px-4 pb-3">
        <div
          v-for="item in preferenceRows"
          :key="item.key"
          class="flex items-start justify-between gap-3 text-sm"
        >
          <span class="shrink-0 text-n-slate-10">{{ item.label }}</span>
          <span class="min-w-0 text-right text-n-slate-11">
            {{ formatValue(item.value) }}
          </span>
        </div>
      </div>
    </div>
  </section>
</template>
